import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import CodeIcon from '@mui/icons-material/Code';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';

interface AlgorithmsModalProps {
  open: boolean;
  onClose: () => void;
  includeSflaGaBaseline?: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface ModalTab {
  name: string;
  key: string;
  type: 'algorithm' | 'comparison';
}

interface ScientificComparisonRow {
  algorithm: string;
  family: string;
  structure: string;
  exploration: string;
  exploitation: string;
  convergence: string;
  quality: string;
  scientificNote: string;
  bestUse: string;
}

interface EmpiricalRankingRow {
  criterion: string;
  ranking: string;
  scope: string;
}

interface ServiceCompositionExample {
  scenario: string;
  steps: string[];
  outcome: string;
  realSample: {
    bindings: string[];
    metrics: string[];
    note: string;
  };
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

// Theory flowcharts for algorithm families and project-specific hybrids.
const algorithmDiagrams: Record<string, string> = {
  'Genetic Algorithm': `                    START
                     │
                     ▼
        ┌────────────────────────┐
        │ Initialize Population  │
        │ - Random chromosomes   │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Evaluate Population     │
        │ - Compute fitness       │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Termination Reached?   │
        └────────────────────────┘
              │
         ┌────┴────┐
        YES        NO
         │          │
         ▼          ▼
┌───────────────┐  ┌────────────────────────┐
│ Return Best   │  │ Select Parents         │
└───────────────┘  │ - According to GA      │
                   │   selection strategy   │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Apply Crossover        │
                   │ - Recombine parents    │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Apply Mutation         │
                   │ - Perturb offspring    │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Evaluate Offspring     │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Form Next Population   │
                   │ - Replacement / elites │
                   └────────────────────────┘
                                │
                                └───────────────↺`,

  'Cultural Algorithm': `                    START
                     │
                     ▼
        ┌────────────────────────┐
        │ Initialize Population   │
        │ and Belief Space        │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Evaluate Population     │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Termination Reached?   │
        └────────────────────────┘
              │
         ┌────┴────┐
        YES        NO
         │          │
         ▼          ▼
┌───────────────┐  ┌────────────────────────┐
│ Return Best   │  │ Select Accepted Set    │
└───────────────┘  │ - Influential members  │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Update Belief Space    │
                   │ - Situational /        │
                   │   normative knowledge  │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Influence Population   │
                   │ - Selection /          │
                   │   crossover / mutation │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Evaluate New Solutions │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Form Updated           │
                   │ Population             │
                   └────────────────────────┘
                                │
                                └───────────────↺`,

  'SFLA': `                    START
                     │
                     ▼
        ┌────────────────────────┐
        │ Initialize Population  │
        │ (Frogs)                │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Evaluate and Rank       │
        │ Population              │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Partition Frogs into    │
        │ Memeplexes              │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Termination Reached?   │
        └────────────────────────┘
              │
         ┌────┴────┐
        YES        NO
         │          │
         ▼          ▼
┌───────────────┐  ┌────────────────────────┐
│ Return Best   │  │ For Each Memeplex      │
└───────────────┘  │ - Identify best /      │
                   │   worst frogs          │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Improve Worst Frog     │
                   │ - Toward local best    │
                   │ - Else toward global   │
                   │   best                 │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Replace or Randomly    │
                   │ Reinitialize Worst     │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Shuffle Frogs Across   │
                   │ Memeplexes             │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Re-rank and Reform     │
                   │ Memeplexes             │
                   └────────────────────────┘
                                │
                                └───────────────↺`,

  'SFLA-GA': `                  START
                     │
                     ▼
        ┌────────────────────────┐
        │ Initialize Population  │
        │ and Evaluate Frogs     │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Rank Population and    │
        │ Form Memeplexes        │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Termination Reached?   │
        └────────────────────────┘
              │
         ┌────┴────┐
        YES        NO
         │          │
         ▼          ▼
┌───────────────┐  ┌────────────────────────┐
│ Return Best   │  │ For Each Memeplex      │
└───────────────┘  │ - Identify local best  │
                   │   and worst frog       │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Improve Worst Frog     │
                   │ - Toward local best    │
                   │ - Else global best     │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Apply GA Operators     │
                   │ - Selection            │
                   │ - Crossover            │
                   │ - Mutation             │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Evaluate Children and  │
                   │ Replace Weaker Frogs   │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Shuffle Frogs and      │
                   │ Reform Memeplexes      │
                   └────────────────────────┘
                                │
                                └───────────────↺`,

  'SFLAGA': `                    START
                     │
                     ▼
        ┌────────────────────────┐
        │ Initialize Population  │
        │ and Evaluate Frogs      │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Rank Population and     │
        │ Form Memeplexes         │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Termination Reached?   │
        └────────────────────────┘
              │
         ┌────┴────┐
        YES        NO
         │          │
         ▼          ▼
┌───────────────┐  ┌────────────────────────┐
│ Return Best   │  │ For Each Memeplex      │
└───────────────┘  │ - Identify local best  │
                   │   and weak members     │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ SFLA Local Improvement │
                   │ - Improve weakest frog │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ GA Search Within       │
                   │ Memeplex               │
                   │ - Selection            │
                   │ - Crossover            │
                   │ - Mutation             │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Evaluate and Replace   │
                   │ Weaker Frogs           │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Shuffle and Reform     │
                   │ Memeplexes             │
                   └────────────────────────┘
                                │
                                └───────────────↺`,

  'PSO': `                    START
                     │
                     ▼
        ┌────────────────────────┐
        │ Initialize Swarm        │
        │ - Positions            │
        │ - Velocities           │
        │ - Personal bests       │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Evaluate Swarm          │
        │ Find Global Best        │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Termination Reached?   │
        └────────────────────────┘
              │
         ┌────┴────┐
        YES        NO
         │          │
         ▼          ▼
┌───────────────┐  ┌────────────────────────┐
│ Return Best   │  │ Update Parameters      │
└───────────────┘  │ - Inertia /            │
                   │   acceleration terms   │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ For Each Particle      │
                   │ - Update velocity      │
                   │ - Update position      │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Evaluate Particle      │
                   │ and Update pBest       │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Update Global Best     │
                   └────────────────────────┘
                                │
                                └───────────────↺`,

  'GAPSO': `                    START
                     │
                     ▼
        ┌────────────────────────┐
        │ Initialize Swarm        │
        │ Evaluate Swarm          │
        │ Find Global Best        │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Termination Reached?   │
        └────────────────────────┘
              │
         ┌────┴────┐
        YES        NO
         │          │
         ▼          ▼
┌───────────────┐  ┌────────────────────────┐
│ Return Best   │  │ PSO Phase              │
└───────────────┘  │ - Update velocities    │
                   │ - Update positions     │
                   │ - Update pBest         │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ GA Phase               │
                   │ - Select particles     │
                   │ - Recombine            │
                   │ - Fallback mutation    │
                   │ - Generate offspring   │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Evaluate and Replace   │
                   │ Improved Particles     │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Update Global Best     │
                   └────────────────────────┘
                                │
                                └───────────────↺`,

};

const algorithmSteps: Record<string, string[]> = {
  'Genetic Algorithm': [
    'Initialize a random population of chromosomes.',
    'Evaluate the initial population before evolution starts.',
    'At each generation, sort the population by fitness and copy the top 10% as elites.',
    'Fill the rest of the next generation by selecting parents with tournament selection or random selection, depending on `selectionRate`.',
    'Apply single-point crossover with probability `crossoverRate`; otherwise copy the parents directly.',
    'Mutate each child independently with probability `mutationRate`.',
    'Evaluate each child once after crossover and mutation are complete.',
    'Replace the old population with the newly built generation.',
    'Update progress with the best chromosome and repeat until the generation limit is reached.',
  ],
  'Cultural Algorithm': [
    'Initialize and evaluate the population, then initialize an explicit belief space with situational and normative knowledge containers.',
    'At each generation, sort the population and take the top accepted set to update the belief space.',
    'Set the best accepted chromosome as situational knowledge and compute the most frequent service choice per task as normative knowledge.',
    'Copy the elite subset directly into the next generation.',
    'Fill the rest of the next generation by selecting parents with the shared `selectionRate` policy and applying crossover when the crossover test succeeds.',
    'Influence offspring with belief-space guidance before mutation, using situational and normative knowledge to adjust selected tasks.',
    'Apply mutation with probability `mutationRate`, evaluate offspring, and build the next generation generationally.',
    'Report the current best chromosome and repeat until the generation limit is reached.',
  ],
  'SFLA': [
    'Initialize the frog population and evaluate all frogs.',
    'Sort the population globally and divide it into memeplexes with round-robin distribution.',
    'At each generation, find the current global best frog.',
    'Inside each memeplex, sort frogs from best to worst.',
    'Try to improve the worst frog by copying a bounded set of genes from the memeplex best.',
    'If that fails, try the same update toward the global best frog.',
    'If both attempts fail, replace the worst frog with a new random solution.',
    'After all memeplexes are processed, shuffle frogs back into the population.',
    'Sort the population again and recreate memeplexes for the next generation.',
    'Report the best frog and repeat until the generation limit is reached.',
  ],
  'SFLA-GA': [
    'Initialize the frog population, evaluate all chromosomes, and sort the population by fitness.',
    'Distribute frogs into memeplexes with round-robin assignment.',
    'At each generation, identify the current global best frog.',
    'Within each memeplex, improve the worst frog by guided copying from the local best and, if needed, the global best.',
    'If both guided refinement attempts fail, replace the worst frog with a newly evaluated random chromosome.',
    'Select parent frogs with the shared selection-rate policy and apply crossover inside the memeplex.',
    'Mutate offspring with the configured mutation rate, evaluate them, and replace weaker frogs only when offspring improve fitness.',
    'Shuffle memeplexes back into the global population, re-sort frogs, and reform memeplexes for the next generation.',
    'Report the best chromosome and repeat until the generation limit is reached.',
  ],
  'SFLAGA': [
    'Initialize and evaluate the population, sort it by fitness, and create memeplexes.',
    'At each generation, use the current best sorted chromosome as the global guide for memeplex evolution.',
    'Within each memeplex, sort frogs and protect an optional elite subset from child and mutation replacement.',
    'Improve the worst frog with multiple local-search attempts using the memeplex best and, when needed, the global best guide.',
    'If guided attempts fail, try a partially randomized hybrid frog and accept it only if it improves the worst frog.',
    'Generate offspring inside each memeplex with guided parent selection that can draw from the global best, memeplex best, or tournament-selected frogs depending on generation progress.',
    'Apply guided mutation to selected non-elite frogs and accept it when it improves fitness or meets the configured near-improvement exploration threshold.',
    'Use greedy survivor selection to replace non-elite weak frogs only when crossover children improve the target frog.',
    'Shuffle memeplexes back into the global population, optionally insert adaptive immigrants during stagnation, then re-sort and reform memeplexes.',
    'Report the best chromosome and repeat until the generation limit is reached.',
  ],
  'PSO': [
    'Initialize the swarm with random positions, zero velocities, and initial personal bests.',
    'Evaluate the initial swarm and set each particle\'s personal-best fitness.',
    'Find the initial global best particle.',
    'At each generation, compute the linearly decreasing inertia weight.',
    'For each particle, update the conceptual velocity with inertia, cognitive, and social terms, then clamp it.',
    'Convert the velocity to a sigmoid transfer probability and use it to decide whether the particle moves toward its personal best or the global best.',
    'Apply a small random exploration fallback when the transfer test does not move the particle.',
    'Evaluate the updated particle.',
    'Update the personal best when the new fitness is better, then refresh the swarm-wide global best after the swarm pass.',
    'Report the best particle and repeat until the generation limit is reached.',
  ],
  'GAPSO': [
    'Initialize the swarm, evaluate all particles, and record the initial global best.',
    'At each generation, compute the inertia weight for the PSO phase.',
    'Run the PSO update over the whole swarm: update velocities, update positions, re-evaluate particles, and refresh personal bests.',
    'Enter the GA hybrid phase and select parent particles with the shared `selectionRate` policy.',
    'Apply crossover to parent positions when the crossover-probability test succeeds.',
    'For crossover children that do not improve their parent, try random-gene fallback mutation with probability `mutationRate` and keep it only if it improves the child.',
    'Convert crossover children into new particles, evaluate them, and inherit or update their personal-best memory.',
    'Replace parent particles only when the corresponding child is fitter.',
    'Refresh the global best, report progress, and repeat until the generation limit is reached.',
  ],
};

const serviceCompositionExamples: Record<string, ServiceCompositionExample> = {
  'Genetic Algorithm': {
    scenario: 'A mobile shopping workflow has four tasks: login, catalog search, payment, and notification. Each task can be bound to one candidate web service from the QWS-derived repository.',
    steps: [
      'Encode one candidate composition as a chromosome such as [service 12, service 304, service 88, service 145].',
      'Create many random chromosomes, then score each one with the current QoS or energy-aware fitness function.',
      'Keep the best compositions, select parent compositions, exchange service choices with crossover, and mutate occasional task-service bindings.',
    ],
    outcome: 'After several generations, the best chromosome represents the selected service chain for the workflow.',
    realSample: {
      bindings: [
        'Task 1 login -> service #938 Service1',
        'Task 2 catalog search -> service #2266 SendEmailService',
        'Task 3 payment -> service #1455 ImgCutout',
        'Task 4 delivery quote -> service #169 DataEnhancement',
        'Task 5 notification -> service #736 eBayWatcherService',
      ],
      metrics: [
        'Best chromosome: [938, 2266, 1455, 169, 736]',
        'Fitness: 0.910937',
        'Total response time: 512.77 ms',
        'Estimated energy: 0.9667 J',
        'Estimated cost: 0.013952',
        'Average availability: 92.83%',
        'Bottleneck throughput: 18.81',
      ],
      note: 'Actual GA best solution from a completed deterministic sample run: 5 tasks, seed 20260418, run seed 1529460267, population 50, iterations 40.',
    },
  },
  'Cultural Algorithm': {
    scenario: 'A health-check workflow needs services for patient lookup, appointment booking, payment, and confirmation, with repeated runs revealing service choices that often perform well.',
    steps: [
      'Start with a population of candidate service chains and evaluate their fitness.',
      'Use the accepted top compositions to update situational knowledge with the best full chain and normative knowledge with the most frequent good service per task.',
      'Generate new candidate chains through crossover and mutation, then let the belief space adjust selected task bindings toward proven services.',
    ],
    outcome: 'The final composition is guided by both population search and accumulated knowledge about reliable service choices per workflow task.',
    realSample: {
      bindings: [
        'Task 1 patient lookup -> service #938 Service1',
        'Task 2 appointment booking -> service #551 LittServiceService',
        'Task 3 payment -> service #2197 LittServiceService',
        'Task 4 records update -> service #884 ARTSPDF_Online_Collaboration',
        'Task 5 confirmation -> service #1481 GuspmService',
      ],
      metrics: [
        'Best chromosome: [938, 551, 2197, 884, 1481]',
        'Fitness: 0.910723',
        'Total response time: 650.80 ms',
        'Estimated energy: 1.2294 J',
        'Estimated cost: 0.015316',
        'Average availability: 89.12%',
        'Bottleneck throughput: 19.95',
      ],
      note: 'Actual Cultural Algorithm best solution from the same completed deterministic sample run.',
    },
  },
  SFLA: {
    scenario: 'A travel-booking workflow needs flight search, hotel search, payment, and itinerary notification services, and the search space is split into memeplexes of candidate compositions.',
    steps: [
      'Represent each frog as a complete service composition and rank all frogs by fitness.',
      'Divide frogs into memeplexes so each subgroup explores a different region of the service-selection space.',
      'Inside each memeplex, improve the weakest composition by copying selected task bindings from the local best frog, then from the global best if needed.',
    ],
    outcome: 'The algorithm returns the strongest service composition discovered after repeated local improvement and global shuffling.',
    realSample: {
      bindings: [
        'Task 1 flight search -> service #938 Service1',
        'Task 2 hotel search -> service #82 HelloWorld',
        'Task 3 payment -> service #822 DOTSContactValidation',
        'Task 4 itinerary build -> service #169 DataEnhancement',
        'Task 5 itinerary notification -> service #622 VasService',
      ],
      metrics: [
        'Best frog: [938, 82, 822, 169, 622]',
        'Fitness: 0.904621',
        'Total response time: 521.11 ms',
        'Estimated energy: 0.9459 J',
        'Estimated cost: 0.014067',
        'Average availability: 91.87%',
        'Bottleneck throughput: 18.74',
      ],
      note: 'Actual SFLA best solution from the same completed deterministic sample run.',
    },
  },
  'SFLA-GA': {
    scenario: 'An e-commerce workflow has separate service choices for authentication, product lookup, payment, delivery quote, and customer notification.',
    steps: [
      'Use SFLA memeplexes to improve weak service chains toward local and global best compositions.',
      'Within each memeplex, select parent compositions and apply single-point crossover to exchange groups of task-service bindings.',
      'Mutate child compositions and replace weaker non-elite frogs only when the child has better fitness.',
    ],
    outcome: 'The baseline hybrid returns a composition shaped by both memeplex refinement and standard GA recombination.',
    realSample: {
      bindings: [
        'Task 1 authentication -> service #627 SOAP',
        'Task 2 product lookup -> service #1657 ServicioSuma',
        'Task 3 payment -> service #551 LittServiceService',
        'Task 4 delivery quote -> service #169 DataEnhancement',
        'Task 5 customer notification -> service #1096 HWService',
      ],
      metrics: [
        'Best frog/chromosome: [627, 1657, 551, 169, 1096]',
        'Fitness: 0.902663',
        'Total response time: 819.98 ms',
        'Estimated energy: 1.4951 J',
        'Estimated cost: 0.016876',
        'Average availability: 87.02%',
        'Bottleneck throughput: 17.92',
      ],
      note: 'Actual SFLA-GA baseline best solution from the same completed deterministic sample run.',
    },
  },
  SFLAGA: {
    scenario: 'A mobile cloud workflow must select services for upload, preprocessing, analysis, storage, and user response while balancing QoS quality with energy consumption.',
    steps: [
      'Use the current global best and memeplex best compositions as guides for local improvement of weak frogs.',
      'Generate guided crossover children from the global best, memeplex best, or tournament-selected compositions as the search progresses.',
      'Apply guided mutation, similarity-aware diversification, and adaptive immigrant refresh when the search stagnates.',
    ],
    outcome: 'The returned composition is the proposed enhanced SFLA-GA solution, emphasizing stronger exploitation while keeping exploration mechanisms active.',
    realSample: {
      bindings: [
        'Task 1 upload -> service #1313 getJoke',
        'Task 2 preprocessing -> service #725 ProTagService',
        'Task 3 analysis -> service #1313 getJoke',
        'Task 4 storage -> service #2413 EmptySA',
        'Task 5 user response -> service #1199 net.xmethods.services.stockquote.StockQuoteService',
      ],
      metrics: [
        'Best frog/chromosome: [1313, 725, 1313, 2413, 1199]',
        'Fitness: 0.929846',
        'Total response time: 784.73 ms',
        'Estimated energy: 1.4536 J',
        'Estimated cost: 0.016844',
        'Average availability: 92.66%',
        'Bottleneck throughput: 27.55',
      ],
      note: 'Actual proposed SFLAGA best solution from the same completed deterministic sample run.',
    },
  },
  PSO: {
    scenario: 'A logistics workflow needs quote, routing, payment, and tracking services, and each particle represents one complete set of service choices.',
    steps: [
      'Initialize particles as random service chains and store each particle personal-best composition.',
      'Use discrete velocity and sigmoid-guided movement to decide whether each task binding moves toward the particle best or global best service choice.',
      'Occasionally use random exploration to avoid keeping the same binding when the particle does not follow a guide.',
    ],
    outcome: 'The final global-best particle gives the selected service chain found by swarm-style movement through the discrete composition space.',
    realSample: {
      bindings: [
        'Task 1 quote -> service #938 Service1',
        'Task 2 routing -> service #2484 SmartPayments',
        'Task 3 payment -> service #1096 HWService',
        'Task 4 tracking -> service #1754 needleService',
        'Task 5 confirmation -> service #2424 Shipment',
      ],
      metrics: [
        'Best particle position: [938, 2484, 1096, 1754, 2424]',
        'Fitness: 0.895632',
        'Total response time: 694.18 ms',
        'Estimated energy: 1.3095 J',
        'Estimated cost: 0.015547',
        'Average availability: 88.65%',
        'Bottleneck throughput: 13.15',
      ],
      note: 'Actual PSO best solution from the same completed deterministic sample run.',
    },
  },
  GAPSO: {
    scenario: 'A media-processing workflow must bind upload, transcoding, thumbnail generation, storage, and notification tasks to efficient candidate services.',
    steps: [
      'Run the PSO phase so particles move toward personal-best and global-best service compositions.',
      'Run a GA phase by selecting particle parents, crossing over their service-position arrays, and trying fallback mutation when crossover does not improve a child.',
      'Convert fitter crossover/fallback-mutation children back into particles while preserving or updating personal-best memory.',
    ],
    outcome: 'The final result is a service composition found by combining swarm guidance with GA-style recombination and conservative mutation.',
    realSample: {
      bindings: [
        'Task 1 upload -> service #938 Service1',
        'Task 2 transcoding -> service #1606 Admin',
        'Task 3 thumbnail generation -> service #854 AffiliateService',
        'Task 4 storage -> service #2413 EmptySA',
        'Task 5 notification -> service #1096 HWService',
      ],
      metrics: [
        'Best particle position: [938, 1606, 854, 2413, 1096]',
        'Fitness: 0.891178',
        'Total response time: 522.00 ms',
        'Estimated energy: 0.9744 J',
        'Estimated cost: 0.014023',
        'Average availability: 92.87%',
        'Bottleneck throughput: 9.93',
      ],
      note: 'Actual GAPSO best solution from the same completed deterministic sample run.',
    },
  },
};

const scientificMethodology = [
  'All baseline algorithms are compared on the same QoS-aware service composition problem with identical dataset structure and consistent parameter conventions.',
  'In energy-aware mode, optimization uses a weighted composite fitness that combines QoS quality and normalized energy consumption, while cost, response time, and execution time are reported separately.',
  'For stochastic algorithms, median across repeated runs is the preferred comparison statistic because it is more robust to outliers than mean.',
  'For the discrete service-selection search space, the swarm methods are implemented as discrete/categorical adaptations rather than literal continuous PSO.',
  'Scientific comparison should separate algorithm structure from empirical results, because rankings can shift with task count, search budget, and hybridization overhead.',
];

const scientificTakeaways = [
  'The current benchmark set includes both a standard SFLA-GA baseline and a separate proposed SFLAGA variant.',
  'SFLAGA adds elite preservation and stronger memeplex-level refinement on top of the standard SFLA-GA backbone.',
  'PSO and Cultural are structurally lighter, while the hybrids trade additional overhead for added guidance or recombination power.',
];

const scopeNotes = [
  'The seven-algorithm thesis comparison covers GA, Cultural, SFLA, SFLA-GA, SFLAGA, PSO, and GAPSO.',
  'When the optional SFLA-GA baseline is disabled, the active comparison covers six algorithms: GA, Cultural, SFLA, SFLAGA, PSO, and GAPSO.',
  'Quality and runtime claims should be read as implementation guidance unless they are backed by exported benchmark results for the same configuration.',
];

const scientificComparisonRows: ScientificComparisonRow[] = [
  {
    algorithm: 'Genetic Algorithm',
    family: 'Evolutionary search',
    structure: 'Flat chromosome population with crossover and mutation',
    exploration: 'High',
    exploitation: 'Medium',
    convergence: 'Medium',
    quality: 'High on broad search spaces',
    scientificNote: 'Strong global exploration, but can converge prematurely without extra structure.',
    bestUse: 'Large search spaces when broad exploration matters more than fast convergence.',
  },
  {
    algorithm: 'Cultural Algorithm',
    family: 'Evolutionary + belief-space guidance',
    structure: 'Flat population with explicit situational and normative belief space',
    exploration: 'Medium',
    exploitation: 'High',
    convergence: 'Fast',
    quality: 'Medium to high',
    scientificNote: 'Belief-space guidance improves exploitation and makes the cultural mechanism explicit instead of approximating it with elite bias alone.',
    bestUse: 'Limited compute budgets and cases where strong exploitation is preferred.',
  },
  {
    algorithm: 'SFLA',
    family: 'Memeplex-based metaheuristic',
    structure: 'Population partitioned into memeplexes with local worst-frog improvement',
    exploration: 'High',
    exploitation: 'Low to medium',
    convergence: 'Slow',
    quality: 'Medium',
    scientificNote: 'Good diversification through memeplexes, but weaker exploitation makes it slower and less competitive than the best hybrids.',
    bestUse: 'Difficult search spaces where diversification is more important than runtime.',
  },
  {
    algorithm: 'SFLA-GA',
    family: 'Standard SFLA + GA hybrid',
    structure: 'Memeplex search with guided worst-frog updates plus standard GA selection, crossover, and mutation',
    exploration: 'High',
    exploitation: 'High',
    convergence: 'Medium to fast',
    quality: 'High',
    scientificNote: 'Serves as the defensible baseline hybrid for comparison with the proposed SFLAGA enhancement.',
    bestUse: 'Baseline hybrid benchmarking when the study needs a standard SFLA+GA reference point.',
  },
  {
    algorithm: 'SFLAGA',
    family: 'Enhanced SFLA + GA hybrid',
    structure: 'Memeplexes plus elite preservation, multi-trial local search, and greedy survivor selection',
    exploration: 'High',
    exploitation: 'Very high',
    convergence: 'Fast and stable',
    quality: 'Very high',
    scientificNote: 'Proposed thesis variant that keeps the SFLA+GA backbone but adds explicit enhancement mechanisms instead of claiming to be the plain standard hybrid.',
    bestUse: 'Proposed-method evaluation when stronger exploitation and protected convergence are desired.',
  },
  {
    algorithm: 'PSO',
    family: 'Swarm intelligence',
    structure: 'Particles with velocity, personal best, and global best in a discrete/categorical adaptation',
    exploration: 'Low',
    exploitation: 'High',
    convergence: 'Fastest',
    quality: 'Medium to low',
    scientificNote: 'Implemented as a discrete PSO adaptation using sigmoid-guided movement, which is more defensible for categorical service selection than the earlier heuristic move rule.',
    bestUse: 'Quick approximate results and runtime-sensitive scenarios.',
  },
  {
    algorithm: 'GAPSO',
    family: 'PSO + GA hybrid',
    structure: 'Swarm search followed by GA-style crossover and fallback mutation over particles',
    exploration: 'Medium',
    exploitation: 'High',
    convergence: 'Fast',
    quality: 'High',
    scientificNote: 'A pragmatic compromise: faster than pure GA-style search with stronger final quality than pure PSO.',
    bestUse: 'Balanced performance when both speed and final quality matter.',
  },
];

const empiricalRankingRows: EmpiricalRankingRow[] = [
  {
    criterion: 'Comparison protocol',
    ranking: 'Same dataset class, repeated runs, median-first analysis, and energy as the primary decision metric.',
    scope: 'Methodology used in the project comparison documents.',
  },
  {
    criterion: 'Canonical status in current build',
    ranking: 'GA, SFLA, and SFLA-GA are the baseline family references; PSO is a discrete adaptation; SFLAGA and GAPSO are enhanced/project variants.',
    scope: 'Current implementation structure after the fairness and canonicalization cleanup.',
  },
  {
    criterion: 'Runtime interpretation',
    ranking: 'Lighter single-family methods generally have less structural overhead than the hybrids, but the actual ordering must be verified empirically for each task range.',
    scope: 'Interpretation rule, not a fixed current ranking.',
  },
  {
    criterion: 'Quality interpretation',
    ranking: 'Hybrids often improve search guidance or diversification, but quality claims should come from the exported experiment results rather than static documentation.',
    scope: 'Interpretation rule for current and future experiments.',
  },
  {
    criterion: 'Interpretation caution',
    ranking: 'The current modal should not be read as a substitute for benchmark exports when making thesis claims.',
    scope: 'Evidence-strength caution for documentation versus measured results.',
  },
];

const AlgorithmsModal: React.FC<AlgorithmsModalProps> = ({
  open,
  onClose,
  includeSflaGaBaseline = false,
}) => {
  const theme = useTheme();
  const [value, setValue] = useState(0);
  const [isMaximized, setIsMaximized] = useState(false);

  const modalTabs: ModalTab[] = [
    { name: 'Genetic Algorithm', key: 'Genetic Algorithm', type: 'algorithm' },
    { name: 'Cultural Algorithm', key: 'Cultural Algorithm', type: 'algorithm' },
    { name: 'SFLA', key: 'SFLA', type: 'algorithm' },
    ...(includeSflaGaBaseline ? [{ name: 'SFLA-GA', key: 'SFLA-GA', type: 'algorithm' as const }] : []),
    { name: 'SFLAGA', key: 'SFLAGA', type: 'algorithm' },
    { name: 'PSO', key: 'PSO', type: 'algorithm' },
    { name: 'GAPSO', key: 'GAPSO', type: 'algorithm' },
    { name: 'Scientific Comparison', key: 'Scientific Comparison', type: 'comparison' },
  ];
  const visibleScientificTakeaways = includeSflaGaBaseline
    ? scientificTakeaways
    : [
        'The optional standard SFLA-GA baseline is currently excluded from the active comparison set.',
        'SFLAGA remains the enhanced SFLA+GA-style variant in the active build.',
        scientificTakeaways[2],
      ];
  const visibleScopeNotes = includeSflaGaBaseline
    ? scopeNotes
    : [
        'The active thesis comparison covers GA, Cultural, SFLA, SFLAGA, PSO, and GAPSO.',
        scopeNotes[1],
        scopeNotes[2],
      ];
  const visibleScientificComparisonRows = includeSflaGaBaseline
    ? scientificComparisonRows
    : scientificComparisonRows.filter((row) => row.algorithm !== 'SFLA-GA');
  const visibleEmpiricalRankingRows = empiricalRankingRows.map((row) => {
    if (!includeSflaGaBaseline && row.criterion === 'Canonical status in current build') {
      return {
        ...row,
        ranking: 'GA and SFLA are the baseline family references; PSO is a discrete adaptation; SFLAGA and GAPSO are enhanced/project variants.',
      };
    }

    return row;
  });

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleToggleMaximize = () => {
    setIsMaximized((prev) => !prev);
  };

  useEffect(() => {
    if (!open) {
      setIsMaximized(false);
    }
  }, [open]);

  useEffect(() => {
    if (value >= modalTabs.length) {
      setValue(Math.max(0, modalTabs.length - 1));
    }
  }, [modalTabs.length, value]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={isMaximized ? false : 'lg'}
      fullWidth
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          width: isMaximized ? '100vw' : undefined,
          maxWidth: isMaximized ? '100vw' : undefined,
          height: isMaximized ? '100vh' : undefined,
          maxHeight: isMaximized ? '100vh' : '90vh',
          borderRadius: isMaximized ? 0 : 2,
          m: isMaximized ? 0 : undefined,
        },
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CodeIcon sx={{ fontSize: 28 }} />
          <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
            Algorithm Flowcharts
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton
            onClick={handleToggleMaximize}
            aria-label={isMaximized ? 'Restore dialog size' : 'Maximize dialog'}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: alpha('#ffffff', 0.1),
              },
            }}
          >
            {isMaximized ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
          </IconButton>
          <IconButton
            onClick={onClose}
            aria-label="Close dialog"
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: alpha('#ffffff', 0.1),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={value}
            onChange={handleChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                minHeight: 48,
              },
            }}
          >
            {modalTabs.map((tab, index) => (
              <Tab key={index} label={tab.name} />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          {modalTabs.map((tab, index) => (
            <TabPanel key={index} value={value} index={index}>
              {tab.type === 'comparison' ? (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    backgroundColor: alpha(theme.palette.background.default, 0.5),
                  }}
                >
                  <Typography variant="h6" gutterBottom sx={{ mb: 1, fontWeight: 600 }}>
                    Scientific Comparison
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 960 }}>
                    This section separates methodology, structural characteristics, and documented empirical trends so the comparison remains evidence-based rather than purely descriptive.
                  </Typography>

                  <Box
                    sx={{
                      display: 'grid',
                      gap: 2,
                      mb: 3,
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: 'repeat(3, minmax(0, 1fr))',
                      },
                    }}
                  >
                    <Paper variant="outlined" sx={{ p: 2.5, backgroundColor: alpha(theme.palette.primary.main, 0.04) }}>
                      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                        Methodology
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2.25, display: 'grid', gap: 1 }}>
                        {scientificMethodology.map((item, itemIndex) => (
                          <Box component="li" key={itemIndex}>
                            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                              {item}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2.5, backgroundColor: alpha(theme.palette.success.main, 0.05) }}>
                      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                        Main Findings
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2.25, display: 'grid', gap: 1 }}>
                        {visibleScientificTakeaways.map((item, itemIndex) => (
                          <Box component="li" key={itemIndex}>
                            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                              {item}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2.5, backgroundColor: alpha(theme.palette.warning.main, 0.07) }}>
                      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                        Scope Notes
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2.25, display: 'grid', gap: 1 }}>
                        {visibleScopeNotes.map((item, itemIndex) => (
                          <Box component="li" key={itemIndex}>
                            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                              {item}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Paper>
                  </Box>

                  <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
                    Algorithm-by-Algorithm Analysis
                  </Typography>
                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{ mb: 3, backgroundColor: theme.palette.background.paper }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Algorithm</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Family</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Population Structure</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Explore</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Exploit</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Convergence</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Quality</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Scientific Note</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Best Use</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {visibleScientificComparisonRows.map((row) => (
                          <TableRow key={row.algorithm} hover>
                            <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{row.algorithm}</TableCell>
                            <TableCell>{row.family}</TableCell>
                            <TableCell>{row.structure}</TableCell>
                            <TableCell>{row.exploration}</TableCell>
                            <TableCell>{row.exploitation}</TableCell>
                            <TableCell>{row.convergence}</TableCell>
                            <TableCell>{row.quality}</TableCell>
                            <TableCell>{row.scientificNote}</TableCell>
                            <TableCell>{row.bestUse}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
                    Documented Empirical Ranking Summary
                  </Typography>
                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{ backgroundColor: theme.palette.background.paper }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Criterion</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Documented Trend</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Evidence Scope</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {visibleEmpiricalRankingRows.map((row) => (
                          <TableRow key={row.criterion} hover>
                            <TableCell sx={{ fontWeight: 600 }}>{row.criterion}</TableCell>
                            <TableCell>{row.ranking}</TableCell>
                            <TableCell>{row.scope}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              ) : (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    backgroundColor: alpha(theme.palette.background.default, 0.5),
                  }}
                >
                  <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                    {tab.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 920, lineHeight: 1.7 }}>
                    The left panel shows a theory-level flowchart for the algorithm family or hybrid design. The right
                    panel summarizes the main steps of this repo&apos;s current implementation.
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 3,
                      alignItems: 'start',
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: 'minmax(0, 1.65fr) minmax(280px, 0.95fr)',
                      },
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 1.5,
                          color: theme.palette.text.secondary,
                          textTransform: 'uppercase',
                          letterSpacing: 0.8,
                        }}
                      >
                        Theory Flowchart
                      </Typography>
                      <Box
                        component="pre"
                        sx={{
                          margin: 0,
                          padding: 3,
                          backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#fafafa',
                          borderRadius: 1,
                          overflow: 'auto',
                          fontSize: '0.8125rem',
                          fontFamily: '"Courier New", Courier, "Lucida Console", Monaco, monospace',
                          lineHeight: 1.5,
                          border: `1px solid ${theme.palette.divider}`,
                          whiteSpace: 'pre',
                          color: theme.palette.text.primary,
                        }}
                      >
                        {algorithmDiagrams[tab.key] || `Diagram for ${tab.name} not available.`}
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        minWidth: 0,
                        position: { md: 'sticky' },
                        top: 0,
                        alignSelf: 'start',
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 1.5,
                          color: theme.palette.text.secondary,
                          textTransform: 'uppercase',
                          letterSpacing: 0.8,
                        }}
                      >
                        Implementation Steps
                      </Typography>
                      <Box
                        sx={{
                          p: 2.5,
                          borderRadius: 1,
                          border: `1px solid ${theme.palette.divider}`,
                          backgroundColor: alpha(theme.palette.primary.main, 0.04),
                        }}
                      >
                        <Box
                          component="ol"
                          sx={{
                            m: 0,
                            pl: 2.75,
                            display: 'grid',
                            gap: 1.25,
                          }}
                        >
                          {(algorithmSteps[tab.key] || []).map((step, stepIndex) => (
                            <Box component="li" key={stepIndex} sx={{ pl: 0.25 }}>
                              <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
                                {step}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {serviceCompositionExamples[tab.key] && (
                    <Box
                      sx={{
                        mt: 3,
                        p: 2.5,
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        backgroundColor: alpha(theme.palette.success.main, 0.045),
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 1,
                          color: theme.palette.text.secondary,
                          textTransform: 'uppercase',
                          letterSpacing: 0.8,
                        }}
                      >
                        Service Composition Example
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.65 }}>
                        {serviceCompositionExamples[tab.key].scenario}
                      </Typography>
                      <Box
                        component="ol"
                        sx={{
                          m: 0,
                          pl: 2.75,
                          display: 'grid',
                          gap: 1,
                        }}
                      >
                        {serviceCompositionExamples[tab.key].steps.map((step, stepIndex) => (
                          <Box component="li" key={stepIndex} sx={{ pl: 0.25 }}>
                            <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
                              {step}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 1.5,
                          lineHeight: 1.65,
                          fontWeight: 600,
                        }}
                      >
                        Result: {serviceCompositionExamples[tab.key].outcome}
                      </Typography>

                      <Box
                        sx={{
                          mt: 2,
                          pt: 2,
                          borderTop: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            mb: 1,
                            fontWeight: 700,
                          }}
                        >
                          Completed Run Best Solution
                        </Typography>
                        <Box
                          sx={{
                            display: 'grid',
                            gap: 2,
                            gridTemplateColumns: {
                              xs: '1fr',
                              md: 'minmax(0, 1.2fr) minmax(260px, 0.8fr)',
                            },
                          }}
                        >
                          <Box>
                            <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600 }}>
                              Task bindings
                            </Typography>
                            <Box component="ul" sx={{ m: 0, pl: 2.25, display: 'grid', gap: 0.75 }}>
                              {serviceCompositionExamples[tab.key].realSample.bindings.map((binding, bindingIndex) => (
                                <Box component="li" key={bindingIndex}>
                                  <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
                                    {binding}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                          <Box>
                            <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600 }}>
                              Measured sample values
                            </Typography>
                            <Box component="ul" sx={{ m: 0, pl: 2.25, display: 'grid', gap: 0.75 }}>
                              {serviceCompositionExamples[tab.key].realSample.metrics.map((metric, metricIndex) => (
                                <Box component="li" key={metricIndex}>
                                  <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
                                    {metric}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, lineHeight: 1.65 }}>
                          {serviceCompositionExamples[tab.key].realSample.note}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Paper>
              )}
            </TabPanel>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AlgorithmsModal;
